import api from 'api';
import { errorMessageFactory } from 'factories/api';
import { normalize, schema } from 'normalizr';
import { fetchDataForAppEditor } from 'redux/modules/apps.actions';
import { actions } from 'redux/modules/clients';
import { getExportableAppIds } from 'redux/modules/invoice.selectors';
import { showToast } from 'redux/modules/toasts.actions';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import {
  createXhrStatusModule,
  xhrStatusFactory,
  xhrStatusUIState,
} from 'redux/reusable/xhrStatus';
import { getAppAccounts } from 'redux/selectors/apps';
import { getClientEntities } from 'redux/selectors/clients';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { Thunk } from 'types/redux';
import { actionCreatorFactory } from 'typescript-fsa';
import { reducerWithInitialState } from 'typescript-fsa-reducers';

const actionCreator = actionCreatorFactory('timely/clients-sync');
const clientSyncXhrModule = createXhrStatusModule('clientSync');
const { wrapFetching, wrapSaving, withXhrStatusReducer } = clientSyncXhrModule;

export { DISALLOWED_CHARACTERS } from 'constants/invoices';

interface AppClient {
  name: string;
  external_id: string;
}

export const fetchedAppClients = actionCreator<{ clients: Array<AppClient>; id: number }>(
  'FETCHED_APP_CLIENTS'
);

export function fetchAppClients(appAccountId: number): Thunk {
  return async dispatch => {
    try {
      const clients = await dispatch(wrapFetching(api.getAppAccountClients(appAccountId)));
      dispatch(fetchedAppClients({ clients, id: appAccountId }));
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
    }
  };
}

export function createClientInAppByConnectionId(
  connectionId: number,
  clientId: number
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const external_references = await dispatch(
      wrapSaving(api.exportClientToAppByConnectionId(connectionId, clientId), clientId.toString())
    );
    const clientEntity = getClientEntities(getState())[clientId];
    const client = { ...clientEntity, external_references };

    dispatch(actions.savedClient({ client }));
  };
}

export function syncClientToApp(
  connectionId: number,
  clientId: number,
  externalId: number
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const external_references = await dispatch(
      wrapSaving(api.syncClientToApp(connectionId, clientId, externalId), clientId.toString())
    );
    const clientEntity = getClientEntities(getState())[clientId];
    const client = { ...clientEntity, external_references };

    dispatch(actions.savedClient({ client }));
  };
}

export function exportClientToApp(clientId: number): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const external_references = await dispatch(wrapSaving(api.exportClientToDefaultApp(clientId)));
    const clientEntity = getClientEntities(getState())[clientId];
    const client = { ...clientEntity, external_references };

    dispatch(actions.savedClient({ client }));
  };
}

export function exportClientToAppByConnectionId(
  connectionId: number,
  clientId: number,
  externalId?: number
): Thunk<Promise<void>> {
  return async dispatch => {
    if (externalId) {
      await dispatch(syncClientToApp(connectionId, clientId, externalId));
      return;
    }
    await dispatch(createClientInAppByConnectionId(connectionId, clientId));
  };
}

interface NormalizedState {
  entities: Record<number, AppClient>;
  result: Array<number>;
}

export interface State {
  appClientLists: Record<number, NormalizedState>;
  xhrStatus: XhrStatus;
}

const initialState: State = {
  appClientLists: {},
  xhrStatus: xhrStatusFactory(),
};

const appClientSchema = new schema.Entity(
  'appClient',
  {},
  {
    idAttribute: value => value.name,
  }
);
const appClientArraySchema = new schema.Array(appClientSchema);

interface AppClientNormalized {
  entities: {
    appClient: {
      [id: number]: AppClient;
    };
  };
  result: Array<number>;
}

const reducer = reducerWithInitialState(initialState).caseWithAction(
  fetchedAppClients,
  (state, action) => {
    const { entities, result }: AppClientNormalized = normalize(
      action.payload.clients,
      appClientArraySchema
    );

    return {
      ...state,
      appClientLists: {
        ...state.appClientLists,
        [action.payload.id]: {
          entities: entities.appClient,
          result,
        },
      },
    };
  }
);

const readAppClientsLists = (state: IState) => state.appClients.appClientLists;
const readXhrStatus = (state: IState) => state.appClients.xhrStatus;

export const makeGetClientXhrStatusStateByKey = () =>
  createSelector(
    [readXhrStatus, (_: unknown, props: { key?: string }) => props.key],
    (xhrStatus, key) => xhrStatusUIState(xhrStatus, key)
  );

export const getAppClientsLists = readAppClientsLists;

export function getAccountsForInvoiceExport(): Thunk {
  return async (dispatch, getState) => {
    const state = getState();
    const appAccounts = getAppAccounts(state);
    const exportableAppIds = getExportableAppIds(state);
    exportableAppIds.forEach(id => {
      dispatch(fetchDataForAppEditor(id)).then(() => {
        const apps = appAccounts[id];
        if (!apps || !apps.length) {
          return;
        }
        apps.forEach(appAccount => {
          dispatch(fetchAppClients(appAccount.id));
        });
      });
    });
  };
}

export default withXhrStatusReducer(reducer);
